import { useState } from "react";
import { firstWorkStep, type SessionKind } from "@studio/shared";
import { InlineSpinner } from "./InlineSpinner";
import { navigate } from "../lib/navigation";
import { pathForStep, saveSession } from "../lib/session";
import { TeacherLoginRequiredError, teacherRequest } from "../lib/teacherApi";

/**
 * 시연작 진입구(§9.H-14). **수업 현황 상세**의 우측 액션 그룹에만 놓는다 —
 * 시연은 교사가 «수업을 하고 있는 자리»에서 켜는 것이다(DESIGN-POLICY v1.10, 2026-08-07).
 *
 * 🔑 옛 위치는 수업 목록(내 수업) 헤더였다. 정책 문구가 오래 "대시보드"라고만 적혀 있어
 *    목록인지 상세인지 모호했던 것이 드리프트의 진원이고, plan·data-class·brand 는 이미 상세다.
 *
 * 🔑 **파생(정책이 받아들인 대가)**: 수업이 0개인 교사는 시연작에 도달할 수 없다. 수업을 먼저 만든다.
 *
 * 🚨 **2026-08-12 개정(B1) — 시연작은 «수업» 이 아니라 «이 수업 안의 작품» 이다.**
 *    종전에는 `POST /api/sessions` 로 **새 수업 세션**을 만들었다. 그래서
 *      · 수업 목록에 수업으로 뜨고 「N개 수업」 카운터에도 세어졌다
 *      · 켤 때마다 하나씩 누적됐다
 *      · 새 세션이라 종류를 다시 물어서, 영상 수업 안에서 켜도 음악 시연이 생겼다
 *    이제 `POST /api/sessions/:id/demo-join` 으로 **열어 놓은 그 수업**에 붙는다.
 *
 * 🔑 그래서 **「무엇을 시연할까요?」 모달이 사라졌다.** 종류는 이 수업이 이미 정했고,
 *    다시 묻는 것은 「이 수업을 시연한다」는 말과 모순이었다. 버튼 하나로 곧장 들어간다.
 *
 * 🚨 한도도 더는 물려보내지 않는다 — 같은 수업이라 «물려받을» 것이 없다.
 *    2026-08-11 에 하드코딩(곡 2개)을 고치며 props 로 흘려보내게 했던 배선인데,
 *    수업이 하나가 되면서 문제 자체가 없어졌다.
 */
export function TeacherDemoLauncher(
  { sessionId, onLoginRequired }: { sessionId: string; onLoginRequired: () => void },
) {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 시연작 시작(§9.H-14 B형, 2026-08-12 개정) — **이 수업에 교사가 참여자로 들어간다.**
   * 그 다음은 학생과 완전히 같은 화면이다(교사 바만 위에 얹힌다).
   *
   * 🚨 새 수업을 만들지 않는다. 종류도 묻지 않는다 — 이 수업이 이미 정했다.
   * 🔑 서버가 학생과 **같은 학생 토큰**을 준다. 다른 것은 `creator_type='teacher'` 하나뿐이고,
   *    그게 정하는 것은 «세는가» 뿐이다(정원·학생 현황에서 빠진다).
   * 🔑 이미 시연작이 있으면 서버가 **그것을 그대로 돌려준다**(같은 이름·같은 creator_type).
   *    그래서 여러 번 눌러도 시연작이 늘지 않는다 — 종전에 수업이 누적되던 자리다.
   */
  const startDemo = async (): Promise<void> => {
    setStarting(true);
    setError(null);
    try {
      const joined = await teacherRequest<{
        student_token: string; work_id: string; kind: SessionKind; code: string;
      }>(
        `/api/sessions/${encodeURIComponent(sessionId)}/demo-join`,
        { method: "POST", body: JSON.stringify({}) },
      );
      saveSession({
        token: joined.student_token,
        workId: joined.work_id,
        name: "선생님",
        code: joined.code,
        hummingEnabled: joined.kind === "music",
        kind: joined.kind,
        demo: true,
        demoSessionId: sessionId,
      });
      navigate(pathForStep(joined.work_id, firstWorkStep(joined.kind)), { replace: true });
    } catch (caught) {
      // 로그인 만료는 이 컴포넌트가 삼키지 않는다 — 페이지가 이미 가진 안내 화면으로 올린다.
      if (caught instanceof TeacherLoginRequiredError) onLoginRequired();
      else setError(caught instanceof Error ? caught.message : "시연작을 시작하지 못했습니다.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <button
        className="button-secondary"
        type="button"
        onClick={() => void startDemo()}
        disabled={starting}
      >
        {starting ? <InlineSpinner label="시연작 준비 중..." /> : "시연작 만들기"}
      </button>
      {/* 실패 안내는 버튼 옆에 남긴다 — 모달이 없어졌으므로 여기가 제자리다. */}
      {error ? <div className="teacher-error-banner" role="alert">{error}</div> : null}
    </>
  );
}
